import * as React from 'react';
import { ChangeEvent } from 'react';

import { useHistory, useParams } from 'react-router-dom';
import MHODateTime from 'domain/dateTime/MHODateTime';
import * as DateInput from 'global_elements/Inputs/TextInput/lib/date';

import DataListsProxy, { DataListType } from 'api/dataLists/dataListsProxy';
import PatientUsersProxy from 'api/patientUsers/patientUsersProxy';

import PageLayout from 'global_elements/Layouts/PageLayout';
import InlineText from 'global_elements/Text/InlineText';
import Paragraph from 'global_elements/Text/Paragraph';
import Button from 'global_elements/Button';
import FlexContainer from 'global_elements/Layouts/FlexContainer';
import LabeledTextInput from 'global_elements/Inputs/TextInput/LabeledTextInput';
import LabeledDateInput from 'global_elements/Inputs/LabeledDateInput';
import LabeledSingleSelect from 'global_elements/Inputs/Dropdown/SingleSelect/LabeledSingleSelect';
import EthnicityLabel from 'global_elements/Text/EthnicityLabel';

import { PageLayoutVariant } from 'global_elements/Layouts/PageLayout/variants';
import { TextInputVariant } from 'global_elements/Inputs/TextInput/variants';
import { ButtonVariant } from 'global_elements/Button/variants';
import { FontColors, FontSizes } from 'global_elements/Text/variants';
import { AlignVariant, DisplayVariant, JustifyVariant } from 'global_elements/Layouts/FlexContainer/variants';
import { AllRoutes } from 'constants/routes';
import { PatientRegistrationState } from 'types/patientAccount';
import { SelectOption, ProgramSelectOption } from 'types/inputProps';
import { SingleStandardDropdownStyles } from 'global_elements/Inputs/Dropdown/SingleSelect/styles';
import { PatientData, PatientID } from 'interfaces/patients/patientData';
import { ListFacility } from 'interfaces/dataLists/listFacility';
import { ListProgram } from 'interfaces/dataLists/listFacilityProgram';
import { NetworkError } from 'api/lib/ApiService/errors';
import { NETWORK_ERROR_MESSAGE } from 'constants/errorMessages';
import { LabeledDatePickerInput } from 'global_elements/Inputs/LabeledDatePickerInput';

type URIParams = {
  patientId: string;
};

const PatientEditPage = (): React.JSX.Element => {
  const [facilityName, setFacilityName] = React.useState<string>('INVALID FACILITY ID');
  const [registrationError, setRegistrationError] = React.useState<string | null>(null);
  const [patientInfo, setPatientInfo] = React.useState<PatientData>();
  const [patientState, setPatientState] = React.useState<PatientRegistrationState>({
    firstName: '',
    lastName: '',
    accountEmail: '',
    accountNumber: '',
    medicalRecordNumber: '',
    DOB: '',
    admissionDate: '',
    program: { label: 'Select a Program', value: '' },
    programOptions: [],
    gender: { label: 'No Selection', value: '0' },
    race: { label: 'No Selection', value: '0' },
    hispan: '',
  });

  const [raceOptions, setRaceOptions] = React.useState<SelectOption[]>([]);
  const [genderOptions, setGenderOptions] = React.useState<SelectOption[]>([]);
  const [selectedAD, setSelectedAD] = React.useState<string>('');
  const [validateDOB, setValidateDOB] = React.useState<boolean>(false);
  const history = useHistory();
  const params = useParams<URIParams>();
  const patientId = parseInt(params.patientId, 10);
  const [facilityId, setFacilityId] = React.useState<number>();

  const fetchOptions = (type: DataListType): Promise<SelectOption[]> => new Promise<SelectOption[]>((resolve, reject) => {
    DataListsProxy.getOptionList(
      type,
      (response) => {
        const options: SelectOption[] = [];

        if (response.data) {
          response.data.forEach((option) => {
            options.push({ label: option.lookupDesc, value: option.lookupID.toString() });
          });
        }

        resolve(options);
      },
      (errorResponse) => {
        console.log(errorResponse);
        reject(errorResponse);
      },
    );
  });

  React.useEffect(() => {
    const p1 = fetchOptions(DataListType.Gender);
    const p2 = fetchOptions(DataListType.Race);
    Promise.all([p1, p2]).then(([genders, races]) => {
      setGenderOptions(genders);
      setRaceOptions(races);
      PatientUsersProxy.getPatientV2(patientId).then((r) => {
        if (r.data) {
          const pat = r.data[0];
          setFacilityId(pat.facilityID);
          const admissionDate = pat.dateAdmitted ? new MHODateTime(pat.dateAdmitted).getFormattedCalendarDate() : '';
          const gender = genders.find((go) => go.value === pat.gender?.toString());
          const race = races.find((ro) => ro.value === pat.race?.toString());
          setPatientState((prevState) => ({
            ...prevState,
            firstName: pat.patientFirstName,
            lastName: pat.patientLastName,
            DOB: pat.dateOfBirth ? new MHODateTime(pat.dateOfBirth).getFormattedCalendarDate() : '',
            admissionDate,
            accountNumber: pat.patientNumber,
            medicalRecordNumber: pat.medicalRecordNumber,
            hispan: pat.hispan,
            gender: gender ?? { label: 'No Selection', value: '0' },
            race: race ?? { label: 'No selection', value: '0' },
          }));
          setSelectedAD(admissionDate);
          setPatientInfo(pat);
        }
      });
    });
  }, [patientId]);

  React.useEffect(() => {
    if (!facilityId) {
      return;
    }
    DataListsProxy.getUserFacilityList()
      .then((facilityList: ListFacility[]) => {
        const foundFacility = facilityList.find((facility) => facility.facilityID === facilityId);
        if (foundFacility) {
          setFacilityName(foundFacility.facilityName);
        }
      })
      .catch((errorResponse) => {
        console.log(errorResponse);
      });
  }, [facilityId]);

  React.useEffect(() => {
    if (!facilityId) {
      return;
    }
    DataListsProxy.getPrograms(facilityId)
      .then((programs: ListProgram[]) => {
        const newProgramOptions: ProgramSelectOption[] = [{ label: 'Select a Program', value: '', maxAge: undefined, minAge: undefined }];
        for (let i = 0; i < programs.length; i += 1) {
          const option = programs[i];

          if (option.programID !== 0) {
            newProgramOptions.push({
              label: option.programName,
              value: option.programID.toString(),
              maxAge: option.maxAge,
              minAge: option.minAge,
              admit4YearExclude: Boolean(option.admit4YearExclude),
            });
          }
        }
        setPatientState((prevState) => ({
          ...prevState,
          programOptions: newProgramOptions,
          program: newProgramOptions.find((o) => o.value === patientInfo?.programID?.toString()) ?? newProgramOptions[0],
        }));
      })
      .catch((errorResponse) => {
        console.log(errorResponse);
      });
  }, [facilityId]);

  const handleADChange = (date: Date | null): void => {
    setSelectedAD(date ? date.toString() : '');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (e.target.name !== 'DOB' && e.target.name !== 'admissionDate') {
      const { name, value } = e.target;
      setPatientState((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    } else if (DateInput.validDateInput(e)) {
      let maskedInput = DateInput.maskInputAsDate(e);
      if (e.target.value.length < patientState.DOB.length) {
        maskedInput = e.target.value;
      }
      if (maskedInput !== undefined) {
        setPatientState((prevState) => ({
          ...prevState,
          [e.target.name]: maskedInput,
        }));
      }
    }
  };

  const handleGenderChange = (option: SelectOption): void => {
    setPatientState((prevState) => ({
      ...prevState,
      gender: option,
    }));
  };

  const handleRaceChange = (option: SelectOption): void => {
    setPatientState((prevState) => ({
      ...prevState,
      race: option,
    }));
  };

  const handleEthnicityChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setPatientState((prevState) => ({
      ...prevState,
      hispan: e.target.value,
    }));
  };

  const invalidDate = (field: 'DOB' | 'admissionDate'): string | undefined => {
    const minAge: number = patientState.program.minAge || 4;
    const maxAge: number = patientState.program.maxAge || 120;
    const ignoreMinAdmissionDate: boolean = patientState.program.admit4YearExclude ?? false;
    const dobMin: Date = new Date(Date.now());
    const dobMax: Date = new Date(Date.now());
    const admissionMinDate: Date = new Date(Date.now());
    const admissionMaxDate: Date = new Date(Date.now());

    dobMin.setFullYear(dobMin.getFullYear() - minAge);
    dobMax.setFullYear(dobMax.getFullYear() - maxAge);
    admissionMaxDate.setDate(admissionMinDate.getDate() + 3);
    admissionMinDate.setFullYear(admissionMaxDate.getFullYear() - 5);

    const fieldValue: string = patientState[field];

    const validYear = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/;
    if (fieldValue !== '' && !new RegExp(validYear).test(fieldValue)) {
      return 'Invalid Date (MM/DD/YYYY)';
    }

    if (fieldValue !== '' && !DateInput.validDate(fieldValue)) {
      return 'Invalid Date Entered';
    }

    if (fieldValue !== '' && field === 'DOB' && new Date(fieldValue) > dobMin) {
      return `Patient must be over ${minAge} years`;
    }

    if (fieldValue !== '' && field === 'DOB' && new Date(fieldValue) < dobMax) {
      return `Patient must be under ${maxAge} years`;
    }

    if (field === 'admissionDate' && (new Date(selectedAD) > admissionMaxDate || (new Date(selectedAD) < admissionMinDate && !ignoreMinAdmissionDate))) {
      return 'Invalid Admission Date';
    }

    // Calculate difference between admission date and DOB
    const diffTime = new Date(selectedAD).getTime() - new Date(patientState.DOB).getTime();
    const diffYears = Math.floor(diffTime / (1000 * 60 * 60 * 24 * 365));

    if (field === 'admissionDate' && diffYears < minAge) {
      return `Patient must be at least ${minAge} years old at time of admission.`;
    }

    return undefined;
  };

  const invalidRegistration = (): boolean => {
    if (
      patientState.program.value === ''
      || patientState.firstName.trim() === ''
      || patientState.lastName.trim() === ''
      || patientState.DOB === ''
      || invalidDate('DOB')
      || selectedAD === ''
      || invalidDate('admissionDate')
      || patientState.accountNumber.trim() === ''
      || patientState.medicalRecordNumber.trim() === ''
      || patientState.gender.value === '0'
      || patientState.race.value === '0'
      || patientState.hispan.trim() === ''
    ) {
      return true;
    }

    return false;
  };

  const cancelRegistration = (): void => {
    history.push(AllRoutes.PATIENT_ACCOUNTS);
  };

  const goToPatientAccountSetup = (patientID: PatientID): void => {
    if (patientID !== -1) {
      history.push(`${AllRoutes.PATIENT_ACCOUNTS}/${patientID}`);
    }
  };

  const createPatientData = async (e: any): Promise<void> => {
    if (!patientInfo?.patientID) {
      return;
    }
    // Disable submit button while processing data
    const submitButton = e.target.closest('button');
    submitButton.disabled = true;

    const formattedDOB: MHODateTime = new MHODateTime(new Date(patientState.DOB));
    const formattedAdmissionDate: MHODateTime = new MHODateTime(new Date(selectedAD?.toString()));

    const race = parseInt(patientState.race.value, 10);
    const gender = parseInt(patientState.gender.value, 10);

    const data: any = {
      patientID: patientInfo.patientID,
      programID: parseInt(patientState.program.value, 10),
      programCode: null,
      patientFirstName: patientState.firstName.trim(),
      patientLastName: patientState.lastName.trim(),
      dateOfBirth: formattedDOB.getDateTimeForPost(),
      patientNumber: patientState.accountNumber.trim(),
      medicalRecordNumber: patientState.medicalRecordNumber.trim(),
      dateAdmitted: formattedAdmissionDate.getDateTimeForPost(),
      StatusID: 1,
      race: race === 0 ? undefined : race,
      gender: gender === 0 ? undefined : gender,
      hispan: patientState.hispan,
    };

    try {
      const response = await PatientUsersProxy.postPatient([data]);

      if (!response.success) {
        throw new Error(response.message);
      }

      if (response.data) {
        goToPatientAccountSetup(response.data);
        setRegistrationError(null);
      }
    } catch (error: any) {
      let { message } = error;

      if (error instanceof NetworkError) {
        message = NETWORK_ERROR_MESSAGE;
      }

      // Re-enable submit button to submit after adressing error
      submitButton.disabled = false;
      console.error(error);
      setRegistrationError(message ?? 'An unexpected error occurred.');
    }
  };

  // ***Patient registration / add patient
  return (
    <PageLayout layout={PageLayoutVariant.PADDED} testText="Patient Edit Page" pageTitle="Edit Patient">
      <FlexContainer display={DisplayVariant.FLEX_COL} align={AlignVariant.START} justify={JustifyVariant.START} extraClasses="patient-registration-content">
        {patientInfo && (
          <>
            <form>
              <FlexContainer display={DisplayVariant.FLEX_ROW} align={AlignVariant.START} justify={JustifyVariant.START} extraClasses="patient-registration-content__first-row">
                <label>
                  <FlexContainer display={DisplayVariant.FLEX_ROW} align={AlignVariant.START} justify={JustifyVariant.START} extraClasses="patient-registration-content__first-row__facility-header">
                    <Paragraph text="Selected Facility" fontColor={FontColors.DARK_GRAY} fontSize={FontSizes.REGULAR} bold />
                  </FlexContainer>
                  <Paragraph text={`${facilityName} (${facilityId})`} fontColor={FontColors.DARK_GRAY} fontSize={FontSizes.REGULAR} />
                </label>
                <label>
                  <FlexContainer display={DisplayVariant.FLEX_ROW} align={AlignVariant.START} justify={JustifyVariant.START}>
                    <Paragraph text="Program" fontColor={FontColors.DARK_GRAY} fontSize={FontSizes.REGULAR} bold />
                  </FlexContainer>
                  <Paragraph text={patientState.program.label} fontColor={FontColors.DARK_GRAY} fontSize={FontSizes.REGULAR} />
                </label>
              </FlexContainer>
              <FlexContainer display={DisplayVariant.FLEX_ROW} align={AlignVariant.START} justify={JustifyVariant.START} extraClasses="patient-registration-content__second-row">
                <LabeledTextInput
                  label="First Name"
                  name="firstName"
                  value={patientState.firstName}
                  onChange={handleInputChange}
                  type="text"
                  variant={TextInputVariant.PRIMARY}
                  testid="first-name-input"
                />
                <LabeledTextInput
                  label="Last Name"
                  name="lastName"
                  value={patientState.lastName}
                  onChange={handleInputChange}
                  type="text"
                  variant={TextInputVariant.PRIMARY}
                  testid="last-name-input"
                />
              </FlexContainer>
              <FlexContainer display={DisplayVariant.FLEX_ROW} align={AlignVariant.START} justify={JustifyVariant.START} extraClasses="patient-registration-content__fourth-row">
                <LabeledDateInput
                  label="Date of Birth"
                  name="DOB"
                  value={patientState.DOB}
                  onChange={handleInputChange}
                  type="text"
                  variant={TextInputVariant.PRIMARY}
                  error={validateDOB || patientState.DOB?.length === 10 ? invalidDate('DOB') : undefined}
                  testid="dob-input"
                  onBlur={() => setValidateDOB(true)}
                />
                {patientInfo?.dateAdmitted && (
                  <LabeledDatePickerInput
                    label="Admission Date"
                    name="admissionDate"
                    initialValue={new Date(patientInfo?.dateAdmitted).toLocaleString('en', { month: '2-digit', day: '2-digit', year: 'numeric' })}
                    onChange={handleADChange}
                    error={invalidDate('admissionDate')}
                    testid="admission-date-input"
                  />
                )}
              </FlexContainer>
              <FlexContainer display={DisplayVariant.FLEX_ROW} align={AlignVariant.START} justify={JustifyVariant.START} extraClasses="patient-registration-content__fifth-row">
                <label>
                  <FlexContainer display={DisplayVariant.FLEX_ROW} align={AlignVariant.START} justify={JustifyVariant.START}>
                    <Paragraph text="Account Number" fontColor={FontColors.DARK_GRAY} fontSize={FontSizes.REGULAR} bold />
                  </FlexContainer>
                  <Paragraph text={patientState.accountNumber} fontColor={FontColors.DARK_GRAY} fontSize={FontSizes.REGULAR} />
                </label>
                <LabeledTextInput
                  label="Medical Record Number"
                  name="medicalRecordNumber"
                  value={patientState.medicalRecordNumber}
                  onChange={handleInputChange}
                  type="text"
                  variant={TextInputVariant.PRIMARY}
                  testid="medical-record-number-input"
                />
              </FlexContainer>
              <FlexContainer display={DisplayVariant.FLEX_ROW} align={AlignVariant.START} justify={JustifyVariant.START} extraClasses="patient-registration-content__third-row">
                {genderOptions.length === 0 && (
                  <LabeledSingleSelect
                    styles={SingleStandardDropdownStyles}
                    label="Sex at Birth"
                    options={[{ label: 'Loading...', value: '' }]}
                    defaultValue={{ label: 'Loading...', value: '' }}
                    value={{ label: 'Loading...', value: '' }}
                    onSelection={handleGenderChange}
                    testid="gender-select"
                  />
                )}
                {patientState.gender && genderOptions.length > 0 && (
                  <LabeledSingleSelect
                    styles={SingleStandardDropdownStyles}
                    label="Sex at Birth"
                    options={genderOptions}
                    defaultValue={patientState.gender}
                    value={patientState.gender}
                    onSelection={handleGenderChange}
                    testid="gender-select"
                  />
                )}
                {raceOptions.length === 0 && (
                  <LabeledSingleSelect
                    styles={SingleStandardDropdownStyles}
                    label="Race"
                    options={[{ label: 'Loading...', value: '' }]}
                    defaultValue={{ label: 'Loading...', value: '' }}
                    value={{ label: 'Loading...', value: '' }}
                    onSelection={handleRaceChange}
                    testid="race-select"
                  />
                )}
                {raceOptions.length > 0 && (
                  <LabeledSingleSelect
                    styles={SingleStandardDropdownStyles}
                    label="Race"
                    options={raceOptions}
                    defaultValue={patientState.race}
                    value={patientState.race}
                    onSelection={handleRaceChange}
                    testid="race-select"
                  />
                )}
              </FlexContainer>
              <FlexContainer display={DisplayVariant.FLEX_ROW} align={AlignVariant.START} justify={JustifyVariant.START}>
                <label>
                  <EthnicityLabel text="Hispanic Ethnicity" fontColor={FontColors.DARK_GRAY} fontSize={FontSizes.REGULAR} bold />
                  <br />
                  <EthnicityLabel text="Is the patient Hispanic, Latino, or Spanish ethnicity?" fontColor={FontColors.DARK_GRAY} fontSize={FontSizes.REGULAR} bold />
                  <EthnicityLabel text="Yes" fontColor={FontColors.DARK_GRAY} fontSize={FontSizes.REGULAR} bold>
                    <input type="radio" id="Hispanic-Ethnicity-rby" name="Hispanic-Ethnicity-list-type-rb" value="Y" defaultChecked={patientInfo.hispan === 'Y'} onChange={handleEthnicityChange} />
                  </EthnicityLabel>
                  <EthnicityLabel text="No" fontColor={FontColors.DARK_GRAY} fontSize={FontSizes.REGULAR} bold testId="hispanic-ethnicity-no-label">
                    <input
                      type="radio"
                      id="Hispanic-Ethnicity-rbn"
                      name="Hispanic-Ethnicity-list-type-rb"
                      value="N"
                      defaultChecked={patientInfo.hispan === 'N'}
                      onChange={handleEthnicityChange}
                      title="Hispanic"
                    />
                  </EthnicityLabel>
                </label>
              </FlexContainer>
            </form>
            <FlexContainer display={DisplayVariant.FLEX_ROW} justify={JustifyVariant.START} align={AlignVariant.START} extraClasses="button-container">
              <Button variant={ButtonVariant.SECONDARY} onClick={cancelRegistration}>
                <InlineText text="Cancel" fontColor={FontColors.BACKGROUND} fontSize={FontSizes.REGULAR} bold />
              </Button>
              <Button variant={ButtonVariant.PRIMARY} onClick={(e) => createPatientData(e)} disabled={invalidRegistration()} testid="submit-button">
                <InlineText text="Submit" fontColor={FontColors.BACKGROUND} fontSize={FontSizes.REGULAR} bold />
              </Button>
            </FlexContainer>
          </>
        )}
        {registrationError && <InlineText text={registrationError} fontColor={FontColors.HIGH_PRIORITY} fontSize={FontSizes.LARGE} bold />}
      </FlexContainer>
    </PageLayout>
  );
};

export default PatientEditPage;
